// useTalkingAvatarJob — follows "Doğal konuşan video" jobs until they finish.
//
// The watcher is module-level on purpose: a job runs 10–30 minutes and must
// keep being followed after the Avatar modal unmounts. While at least one job
// is being watched it holds ONE socket to the middleware root, in the
// training's session room (join_session on every (re)connect — the same
// channel lipsync-v2 uses; the /jobs namespace of useJobSocket is the wrong
// one for this contract), and polls GET /talking-avatar/:id as a fallback:
// every 10 s while the socket is down, every 30 s while it is up. A job with
// no visible change for 60 minutes is shown as "unknown" (polled every 60 s
// from then on); any movement brings it back.
//
// A 403 / 404 from that GET means the job is gone or is not this account's
// (the tab's sessionStorage can outlive a sign-in as someone else): it is
// marked unknown + `unreachable` and never polled again, not even after a
// reload. Only a manual refresh that gets through lifts it.
//
// Completion and failure can arrive twice (worker + gateway) and in different
// shapes; normalizeJobEvent / reduceJobEvent in the store make that idempotent.
// A completion without its result payload triggers one GET.
//
// useTalkingAvatarJobForScene(navId) is what the Avatar tab reads: the scene's
// job from the store, re-watched with an immediate GET when the modal is
// reopened or the page was reloaded (records come back from sessionStorage).
import { useCallback, useEffect } from "react";
import { io as socketIo } from "socket.io-client";
import { getToken } from "../../../../common/environment_config.es6";
import { bearerSocketAuth } from "../../../utils/socketAuth";
import { getTalkingAvatarJob } from "./talkingAvatarApi";
import {
  applyTalkingAvatarJobEvent,
  getStoredTalkingAvatarJob,
  getTalkingAvatarJobs,
  isTerminalStatus,
  normalizeJobEvent,
  upsertTalkingAvatarJob,
  useSceneTalkingAvatarJob,
} from "./talkingAvatarJobStore";

const POLL_SOCKET_DOWN_MS = 10 * 1000;
const POLL_SOCKET_UP_MS = 30 * 1000;
const POLL_STALLED_MS = 60 * 1000;
export const STALL_AFTER_MS = 60 * 60 * 1000;
// A "completed" job whose result never shows up is given up after this many
// fetches; applying it later still fetches the result on its own.
const MAX_RESULT_FETCHES = 10;

const isTalkingType = (t) =>
  String(t || "")
    .toLowerCase()
    .replace(/_/g, "-") === "talking-avatar";

function defaultDeps() {
  return {
    io: socketIo,
    getJob: (jobId) => getTalkingAvatarJob(jobId),
    getToken,
    readRoot: () =>
      String((typeof window !== "undefined" && window.ideaLibraryRootUrl) || "")
        .replace(/\/+$/, "")
        .replace(/\/api$/, ""),
    readTrainingId: () => {
      try {
        return (
          new URLSearchParams(window.location.search).get("id") ||
          window.idealStudioTrainingId ||
          null
        );
      } catch (e) {
        return null;
      }
    },
    now: () => Date.now(),
  };
}

let deps = defaultDeps();
let socket = null;
let socketConnected = false;
let socketTrainingId = null;
let pollTimer = null;
const watched = new Set();
const inFlight = new Map(); // jobId → promise of the running GET
const noResultFetches = {};
let resumedAll = false;

export function configureTalkingAvatarWatcher(overrides) {
  deps = { ...deps, ...(overrides || {}) };
}

const needsWatching = (job) =>
  !!job &&
  !job.test && // "Test üretim": local only, the API never heard of it
  !job.unreachable &&
  (!isTerminalStatus(job.status) || (job.status === "completed" && !job.result));

// The status GET will never answer this job for us (see the header).
const isUnreachableError = (err) => !!err && (err.status === 403 || err.status === 404);

// ── Socket ────────────────────────────────────────────────────────────────
function handleSocketEvent(raw, kind) {
  const ev = normalizeJobEvent(raw, kind);
  if (!ev.jobId) return;
  if (ev.jobType && !isTalkingType(ev.jobType)) return;
  if (!getStoredTalkingAvatarJob(ev.jobId)) return; // not a job this studio started
  const out = applyTalkingAvatarJobEvent(ev.jobId, ev, deps.now());
  if (out.needsResult && !(out.job && out.job.unreachable)) {
    refreshTalkingAvatarJob(ev.jobId).catch(() => {});
  } else if (!needsWatching(out.job)) {
    stopWatching(ev.jobId);
  }
}

function openSocket() {
  if (socket) return;
  const root = deps.readRoot();
  const trainingId = deps.readTrainingId();
  if (!root || !trainingId || !(deps.getToken && deps.getToken())) return; // polling covers it
  socketTrainingId = trainingId;
  socket = deps.io(root, {
    transports: ["websocket", "polling"],
    // Function form: a job can outlive the token it started with.
    auth: bearerSocketAuth(deps.getToken),
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
  });
  socket.on("connect", () => {
    socketConnected = true;
    socket.emit("join_session", { trainingId: socketTrainingId });
    // Catch up on whatever happened while disconnected.
    schedulePoll(0);
  });
  socket.on("disconnect", () => {
    socketConnected = false;
    schedulePoll();
  });
  socket.on("connect_error", (e) => {
    socketConnected = false;
    console.warn("[TalkingAvatar] socket connect_error", e && e.message);
  });
  socket.on("session_job_update", (p) => handleSocketEvent(p, "update"));
  socket.on("session_job_completed", (p) => handleSocketEvent(p, "completed"));
}

function closeSocket() {
  if (!socket) return;
  const s = socket;
  socket = null;
  socketConnected = false;
  try {
    s.emit("leave_session", { trainingId: socketTrainingId });
  } catch (e) {
    // best effort
  }
  s.off();
  s.disconnect();
}

// ── Polling ───────────────────────────────────────────────────────────────
function pollDelay() {
  const jobs = [...watched].map((id) => getStoredTalkingAvatarJob(id)).filter(Boolean);
  if (jobs.length && jobs.every((j) => j.status === "unknown")) return POLL_STALLED_MS;
  return socketConnected ? POLL_SOCKET_UP_MS : POLL_SOCKET_DOWN_MS;
}

function schedulePoll(delay) {
  if (pollTimer) clearTimeout(pollTimer);
  pollTimer = null;
  if (!watched.size) return;
  pollTimer = setTimeout(pollTick, delay != null ? delay : pollDelay());
}

async function pollTick() {
  pollTimer = null;
  for (const jobId of [...watched]) {
    const job = getStoredTalkingAvatarJob(jobId);
    if (!needsWatching(job)) {
      stopWatching(jobId);
      continue;
    }
    try {
      await refreshTalkingAvatarJob(jobId);
    } catch (e) {
      // transient — next tick retries
    }
    const after = getStoredTalkingAvatarJob(jobId);
    if (after && !isTerminalStatus(after.status) && after.status !== "unknown") {
      const now = deps.now();
      const since = Number(after.lastChangeAt) || Number(after.createdAt) || now;
      if (now - since > STALL_AFTER_MS)
        applyTalkingAvatarJobEvent(jobId, { status: "unknown" }, now);
    }
  }
  schedulePoll();
}

function startWatching(jobId) {
  if (!jobId || watched.has(jobId)) return;
  watched.add(jobId);
  openSocket();
  schedulePoll();
}

function stopWatching(jobId) {
  watched.delete(jobId);
  if (!watched.size) {
    if (pollTimer) clearTimeout(pollTimer);
    pollTimer = null;
    closeSocket();
  }
}

// One GET → merged into the store. Concurrent calls for a job share one request.
export function refreshTalkingAvatarJob(jobId) {
  if (!jobId) return Promise.resolve(null);
  if (inFlight.has(jobId)) return inFlight.get(jobId);
  const p = (async () => {
    try {
      let data;
      try {
        data = await deps.getJob(jobId);
      } catch (err) {
        if (!isUnreachableError(err)) throw err; // transient — the next tick retries
        applyTalkingAvatarJobEvent(jobId, { status: "unknown", unreachable: true }, deps.now());
        stopWatching(jobId);
        return getStoredTalkingAvatarJob(jobId);
      }
      const before = getStoredTalkingAvatarJob(jobId);
      const out = applyTalkingAvatarJobEvent(
        jobId,
        { ...normalizeJobEvent(data, "http"), reachable: true },
        deps.now()
      );
      // An unreachable job answered again (a manual refresh): follow it again.
      const reachableAgain = !!(before && before.unreachable && out.job && !out.job.unreachable);
      if (out.needsResult) {
        noResultFetches[jobId] = (noResultFetches[jobId] || 0) + 1;
        if (noResultFetches[jobId] >= MAX_RESULT_FETCHES) stopWatching(jobId);
        else if (reachableAgain) startWatching(jobId);
      } else if (out.job && !needsWatching(out.job)) {
        stopWatching(jobId);
      } else if (reachableAgain) {
        startWatching(jobId);
      }
      return out.job;
    } finally {
      inFlight.delete(jobId);
    }
  })();
  inFlight.set(jobId, p);
  return p;
}

// Record a freshly submitted job and follow it.
export function watchTalkingAvatarJob(record) {
  const job = upsertTalkingAvatarJob(record, deps.now());
  if (job) startWatching(job.jobId);
  return job;
}

// Follow a stored job again (modal reopened / page reloaded) — with a GET now.
export function resumeTalkingAvatarJob(jobId) {
  const job = getStoredTalkingAvatarJob(jobId);
  if (!needsWatching(job)) return Promise.resolve(job);
  startWatching(jobId);
  return refreshTalkingAvatarJob(jobId).catch(() => null);
}

// After a reload the store comes back from sessionStorage with every scene's
// running jobs; the first Avatar tab of the page picks all of them up again.
function resumeAllStoredJobs() {
  if (resumedAll) return;
  resumedAll = true;
  Object.values(getTalkingAvatarJobs()).forEach((j) => {
    if (needsWatching(j)) resumeTalkingAvatarJob(j.jobId);
  });
}

export function useTalkingAvatarJobForScene(navId) {
  useEffect(() => {
    resumeAllStoredJobs();
  }, []);
  // This scene's job only: other jobs' progress ticks do not re-render the tab.
  const job = useSceneTalkingAvatarJob(navId);
  const jobId = job ? job.jobId : null;
  const watch = needsWatching(job);
  useEffect(() => {
    if (jobId && watch) resumeTalkingAvatarJob(jobId);
  }, [jobId, watch]);
  const refresh = useCallback(
    () => (jobId ? refreshTalkingAvatarJob(jobId).catch(() => null) : Promise.resolve(null)),
    [jobId]
  );
  return { job, refresh };
}

// Test helper.
export function __resetTalkingAvatarWatcher() {
  if (pollTimer) clearTimeout(pollTimer);
  pollTimer = null;
  watched.clear();
  closeSocket();
  inFlight.clear();
  Object.keys(noResultFetches).forEach((k) => delete noResultFetches[k]);
  resumedAll = false;
  deps = defaultDeps();
}
